import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import ResponsiveDrawer from '../components/ResponsiveDrawer';
import CircularLoading from "../components/CircularLoading";
import RolesAPI from '../common/rolesApi';

function matchUser(permit, usertype) {
    let permitions = permit ? permit.split(',') : [];
    let permitted = false;
    for (let i = 0; i < permitions.length; i++) {
        permitted = usertype === permitions[i] ? true : false
        if (permitted) break;
    }
    return permitted;
}

async function checkUserPermissions(userProfile, requiredPermissions, currentPage) {
    try {
        console.log('🔐 Checking permissions for user:', userProfile.uid);
        console.log('🔐 Required permissions:', requiredPermissions);
        console.log('🔐 Current page:', currentPage);
        console.log('🔐 User type:', userProfile.usertype);
        console.log('🔐 Custom role:', userProfile.customRole);

        // If no specific permissions required, use default usertype matching
        if (!requiredPermissions || requiredPermissions.length === 0) {
            console.log('🔐 No specific permissions required, allowing access');
            return true;
        }

        // First check if user has custom role (prioritize custom role over usertype)
        const userRoleResponse = await RolesAPI.getUserRole(userProfile.uid);

        if (userRoleResponse.success && userRoleResponse.user) {
            const userRole = userRoleResponse.user;
            console.log('🔐 User role data:', userRole);

            // If user has custom role, check permissions against it (even for admin users)
            if (userRole.customRole && userRole.permissions && userRole.permissions.length > 0) {
                console.log('🔐 User has custom role with permissions:', userRole.permissions);
                console.log('🔐 Custom role takes priority over usertype:', userProfile.usertype);

                // Check if user has permission for this page
                const hasPageAccess = userRole.permissions.includes(currentPage);
                console.log('🔐 Page access check for custom role:', hasPageAccess);

                return hasPageAccess;
            } else {
                console.log('🔐 User has no custom role or permissions, checking usertype');
            }
        }

        // Only if no custom role, then check admin usertype
        if (userProfile.usertype === 'admin') {
            console.log('🔐 Admin user without custom role, allowing full access');
            return true;
        }

        // Fallback to default usertype matching
        console.log('🔐 Falling back to default usertype matching');
        const defaultAccess = matchUser(requiredPermissions, userProfile.usertype);
        console.log('🔐 Default access result:', defaultAccess);
        return defaultAccess;

    } catch (error) {
        console.error('Error checking user permissions:', error);
        // Fallback to default usertype matching on error
        console.log('🔐 Error occurred, falling back to default usertype matching');
        return matchUser(requiredPermissions, userProfile.usertype);
    }
}

function ProtectedRoute({ permit, children }) {
    console.log("🔐 ProtectedRoute for permit:", permit);
    const auth = useSelector(state => state.auth);
    const [checkedAuth, setCheckedAuth] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [isCheckingPermission, setIsCheckingPermission] = useState(false);

    const checkPermissions = useCallback(async () => {
        if (!auth.profile || !auth.profile.uid) return;

        setIsCheckingPermission(true);
        try {
            // Extract current page from URL for permission checking
            const currentPage = window.location.pathname.slice(1); // Remove leading slash
            const pagePermission = currentPage || 'home';

            const permitted = await checkUserPermissions(auth.profile, permit, pagePermission);
            console.log("🔐 Permission check result:", permitted);
            setHasPermission(permitted);
        } catch (error) {
            console.error('Error checking permissions:', error);
            // Fallback to default usertype matching
            setHasPermission(matchUser(permit, auth.profile.usertype));
        } finally {
            setIsCheckingPermission(false);
        }
    }, [auth.profile, permit]);

    useEffect(() => {
        if (auth.profile && auth.profile.uid) {
            setCheckedAuth(true);
            checkPermissions();
        }
        if (auth.error && auth.error.msg && !auth.profile) {
            setCheckedAuth(true);
        }
    }, [auth.profile, auth.error, permit, checkPermissions])



    if (!checkedAuth || isCheckingPermission) {
        return <CircularLoading />;
    }

    if (!auth.profile || !auth.profile.uid) {
        return <Navigate to="/login" />;
    }

    if (!hasPermission) {
        return <Navigate to="/login" />;
    }

    return <ResponsiveDrawer>{children}</ResponsiveDrawer>;
}

export default ProtectedRoute;